import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Heart, Sparkles, Monitor, Users } from 'lucide-react';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  const values = [
    {
      icon: Heart,
      title: 'Faith-Centered',
      description: 'We prioritize the heart of ministry, honoring the message and the mission.',
      color: 'neon-pink'
    },
    {
      icon: Sparkles,
      title: 'Spirit-Led',
      description: 'Every production is handled with reverence and understanding of your ministry.',
      color: 'neon-blue'
    },
    {
      icon: Monitor,
      title: 'Broadcast-Level Quality',
      description: 'Broadcast-quality equipment and expertise without the learning curve.',
      color: 'neon-pink'
    },
    {
      icon: Users,
      title: 'True Partnership',
      description: "We're not just vendors, we're partners in your ministry journey.",
      color: 'neon-blue'
    }
  ];

  const getColorClass = (color, type) => {
    const colors = {
      'neon-blue': type === 'text' ? 'text-neon-blue' : type === 'border' ? 'border-neon-blue' : 'bg-neon-blue',
      'neon-pink': type === 'text' ? 'text-neon-pink' : type === 'border' ? 'border-neon-pink' : 'bg-neon-pink',
    };
    return colors[color] || colors['neon-blue'];
  };

  return (
    <section id="about" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian" data-testid="about-section" ref={ref}>
      {/* Background decoration - Golden/amber line at top */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 md:mb-16"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            Why We're Different
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6 sm:mb-8" data-testid="about-heading">
            This Isn't Just <span className="text-neon-blue">Production</span>
            <br />
            <span className="text-white">It's</span> <span className="text-neon-pink">Partnership</span>
          </h2>
        </motion.div>

        {/* Content with Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center mb-12 sm:mb-16 md:mb-20"
        >
          {/* Text Content */}
          <div className="text-sm sm:text-base md:text-lg text-white/70 space-y-4 sm:space-y-5 md:space-y-6 order-2 lg:order-1">
            <p>
              We specialize in faith-based production because we understand that ministry isn't just content, it's a calling. It carries weight. It carries responsibility. And it deserves to be handled with care.
            </p>
            <p>
              We understand the culture. The language. The moments you can't script. The pauses that matter. The heart behind what's being said. This isn't just another project on our calendar, it's someone's assignment.
            </p>
            <p>
              We're not just here to run cameras, adjust lighting, or manage audio levels. We're here to come alongside you. To support the mission. To remove distractions so you can stay present and focused on what God has called you to share.
            </p>
            <p>
              The message stays front and center. We simply help you steward it well, and deliver it with clarity and excellence.
            </p>
            <p className="mt-6">
              Production Built for Impact.
              <br />
              <span 
                className="uppercase tracking-widest text-sm sm:text-base text-neon-blue"
                style={{ 
                  textShadow: '0 0 20px rgba(0, 240, 255, 0.4), 0 0 40px rgba(0, 240, 255, 0.2)'
                }}
              >The Experience Upgrade</span>
            </p>
          </div>

          {/* Image with Badge - Home Studio Setup */}
          <div className="relative order-1 lg:order-2">
            <img 
              src="https://images.unsplash.com/photo-1668609045515-ebf86dd88c8e?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Home Studio Broadcast Setup"
              className="w-full h-auto rounded-lg"
            />
            {/* Experience Badge */}
            <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-neon-blue text-obsidian px-4 sm:px-6 py-3 sm:py-4 transform -rotate-3">
              <span className="font-heading text-lg sm:text-xl md:text-2xl font-black uppercase">10+ Years</span>
              <br />
              <span className="font-heading text-xs sm:text-sm uppercase tracking-wider">Experience</span>
            </div>
          </div>
        </motion.div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`service-card p-4 sm:p-5 md:p-6 border ${getColorClass(value.color, 'border')} border-opacity-20 hover:border-opacity-50 transition-all`}
              data-testid={`value-card-${index}`}
            >
              <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 border ${getColorClass(value.color, 'border')} mb-4 sm:mb-5 md:mb-6`}>
                <value.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${getColorClass(value.color, 'text')}`} />
              </div>
              <h3 className={`font-heading text-base sm:text-lg font-bold uppercase mb-2 sm:mb-3 ${getColorClass(value.color, 'text')}`}>
                {value.title}
              </h3>
              <p className="text-xs sm:text-sm text-white/60 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Pink Line Separator at Bottom */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-pink to-transparent" />
    </section>
  );
};

export default About;
